import { useEffect, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar, Clock, User, CheckCircle, XCircle, Video, MapPin } from 'lucide-react';
import { format } from 'date-fns';
import { useToast } from '@/components/ui/use-toast';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';

interface SessionRequest {
  id: string;
  client_id: string;
  requested_date: string;
  requested_time_start: string;
  requested_time_end: string;
  session_type: string;
  status: string;
  message: string;
  price: number;
  created_at: string;
  profiles: {
    first_name: string;
    last_name: string;
  };
}

export const SessionRequestsTab = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [requests, setRequests] = useState<SessionRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState<SessionRequest | null>(null);
  const [therapistNotes, setTherapistNotes] = useState('');
  const [isRespondDialogOpen, setIsRespondDialogOpen] = useState(false);
  const [respondAction, setRespondAction] = useState<'approve' | 'reject'>('approve');

  useEffect(() => {
    loadRequests();
  }, [user]);

  const loadRequests = async () => {
    if (!user) return;

    try {
      setLoading(true);

      const { data, error } = await supabase
        .from('session_requests')
        .select('*')
        .eq('therapist_id', user.id)
        .in('status', ['pending', 'approved'])
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Fetch profiles separately
      const clientIds = data?.map(r => r.client_id) || [];
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, first_name, last_name')
        .in('id', clientIds);

      const enrichedData = data?.map(request => ({
        ...request,
        profiles: profiles?.find(p => p.id === request.client_id) || { first_name: '', last_name: '' }
      })) || [];

      setRequests(enrichedData as any);
    } catch (error) {
      console.error('Error loading requests:', error);
      toast({
        title: "Error",
        description: "Failed to load session requests",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const openRespondDialog = (request: SessionRequest, action: 'approve' | 'reject') => {
    setSelectedRequest(request);
    setRespondAction(action);
    setIsRespondDialogOpen(true);
  };

  const handleRespond = async () => {
    if (!selectedRequest) return;

    try {
      if (respondAction === 'approve') {
        // Create session
        const { data: session, error: sessionError } = await supabase
          .from('sessions')
          .insert({
            client_id: selectedRequest.client_id,
            therapist_id: user?.id,
            scheduled_at: `${selectedRequest.requested_date}T${selectedRequest.requested_time_start}`,
            duration_minutes: 60,
            session_type: selectedRequest.session_type as any,
            status: 'scheduled' as any,
            price: selectedRequest.price,
            platform_fee: selectedRequest.price * 0.10,
            therapist_notes: therapistNotes
          } as any)
          .select()
          .single();

        if (sessionError) throw sessionError;

        // Update request
        const { error: updateError } = await supabase
          .from('session_requests')
          .update({
            status: 'approved',
            therapist_notes: therapistNotes,
            responded_at: new Date().toISOString(),
            session_id: session.id
          })
          .eq('id', selectedRequest.id);

        if (updateError) throw updateError;

        toast({
          title: "Request Approved",
          description: "Session has been scheduled successfully",
        });
      } else {
        // Reject request
        const { error } = await supabase
          .from('session_requests')
          .update({
            status: 'rejected',
            therapist_notes: therapistNotes,
            responded_at: new Date().toISOString()
          })
          .eq('id', selectedRequest.id);

        if (error) throw error;

        toast({
          title: "Request Rejected",
          description: "The session request has been declined",
        });
      }

      setIsRespondDialogOpen(false);
      setTherapistNotes('');
      loadRequests();
    } catch (error: any) {
      console.error('Error responding to request:', error);
      toast({
        title: "Error",
        description: "Failed to respond to request",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return <Card><CardContent className="p-8 text-center">Loading requests...</CardContent></Card>;
  }

  const pendingRequests = requests.filter(r => r.status === 'pending');
  const approvedRequests = requests.filter(r => r.status === 'approved');

  return (
    <div className="space-y-6">
      {/* Pending Requests */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-orange-500" />
            Pending Session Requests
            {pendingRequests.length > 0 && (
              <Badge variant="destructive">{pendingRequests.length}</Badge>
            )}
          </CardTitle>
          <CardDescription>
            New booking requests from clients waiting for your response
          </CardDescription>
        </CardHeader>
        <CardContent>
          {pendingRequests.length > 0 ? (
            <div className="space-y-4">
              {pendingRequests.map((request) => (
                <div
                  key={request.id}
                  className="flex items-center justify-between p-4 border rounded-lg bg-orange-50 dark:bg-orange-950/20"
                >
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-orange-100 dark:bg-orange-900 rounded-full">
                      <User className="h-5 w-5 text-orange-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold">
                        {request.profiles.first_name} {request.profiles.last_name}
                      </h4>
                      <div className="flex items-center gap-3 text-sm text-muted-foreground mt-1">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {format(new Date(request.requested_date), 'MMM d, yyyy')}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {request.requested_time_start} - {request.requested_time_end}
                        </span>
                        <span className="flex items-center gap-1">
                          {request.session_type === 'online' ? (
                            <><Video className="h-3 w-3" /> Online</>
                          ) : (
                            <><MapPin className="h-3 w-3" /> In Person</>
                          )}
                        </span>
                      </div>
                      {request.message && (
                        <p className="text-xs text-muted-foreground mt-2 italic">
                          "{request.message}"
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-primary mr-2">${request.price}</span>
                    <Button
                      size="sm"
                      variant="default"
                      onClick={() => openRespondDialog(request, 'approve')}
                    >
                      <CheckCircle className="h-4 w-4 mr-1" />
                      Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openRespondDialog(request, 'reject')}
                    >
                      <XCircle className="h-4 w-4 mr-1" />
                      Decline
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No pending requests</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recently Approved */}
      {approvedRequests.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-500" />
              Recently Approved
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {approvedRequests.slice(0, 3).map((request) => (
                <div
                  key={request.id}
                  className="flex items-center justify-between p-3 border rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-100 dark:bg-green-900 rounded-full">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                    </div>
                    <div>
                      <p className="font-medium text-sm">
                        {request.profiles.first_name} {request.profiles.last_name}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {format(new Date(request.requested_date), 'MMM d, yyyy')} at {request.requested_time_start}
                      </p>
                    </div>
                  </div>
                  <Badge variant="secondary">Scheduled</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Response Dialog */}
      <Dialog open={isRespondDialogOpen} onOpenChange={setIsRespondDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {respondAction === 'approve' ? 'Approve Session Request' : 'Decline Session Request'}
            </DialogTitle>
            <DialogDescription>
              {respondAction === 'approve'
                ? 'This will create a scheduled session with the client.'
                : 'The client will be notified of your decision.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <Textarea
              placeholder="Add notes (optional)"
              value={therapistNotes}
              onChange={(e) => setTherapistNotes(e.target.value)}
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsRespondDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant={respondAction === 'approve' ? 'default' : 'destructive'}
              onClick={handleRespond}
            >
              {respondAction === 'approve' ? 'Approve & Schedule' : 'Decline Request'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
